<!-- Сохранённый текст и время уведомления. Переход не меняет read_at. -->
<template>
  <li class="notification-item" :class="{ 'notification-item--unread': !item.read_at, 'notification-item--compact': compact }">
    <router-link
      v-if="internalLink"
      class="notification-item__link"
      :to="internalLink"
      :aria-label="item.title"
    />
    <span
      class="notification-item__marker"
      :aria-label="item.read_at ? 'Прочитано' : 'Не прочитано'"
      role="img"
    />
    <article class="notification-item__content">
      <div class="notification-item__meta">
        <div v-if="item.groups?.length" class="notification-item__groups">
          <BaseTooltip
            v-for="group in compact ? item.groups.slice(0, 1) : item.groups"
            :key="group.group"
            :text="group.label"
          >
            <span
              class="notification-item__group"
              :role="compact && group.emoji ? 'img' : undefined"
              :aria-label="compact && group.emoji ? group.label : undefined"
            >
              <span v-if="group.emoji" aria-hidden="true">{{ group.emoji }}</span>
              <span v-if="!compact || !group.emoji">{{ group.label }}</span>
            </span>
          </BaseTooltip>
        </div>
        <time class="notification-item__time" :datetime="item.created_at">
          {{ formattedTime }}
        </time>
      </div>
      <h3 class="notification-item__title">
        {{ item.title }}
      </h3>
      <p class="notification-item__body">{{ item.body }}</p>
    </article>
    <ReadState
      class="notification-item__read"
      :read="Boolean(item.read_at)"
      :title="item.title"
      :disabled="disabled"
      :busy="reading"
      @toggle="$emit(item.read_at ? 'mark-unread' : 'mark-read', $event)"
    />
  </li>
</template>

<script>
import { formatDateAndTimeLocal } from "@/utils/dateUtils";
import ReadState from "@/components/Notification/List/ReadState.vue";

export default {
  name: "NotificationListItem",
  components: { ReadState },
  props: {
    item: { type: Object, required: true },
    compact: { type: Boolean, default: false },
    reading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ["mark-read", "mark-unread"],
  computed: {
    formattedTime() {
      return formatDateAndTimeLocal(this.item.created_at);
    },
    internalLink() {
      const link = this.item.link;
      return typeof link === "string" && /^\/(?!\/)/.test(link) && !link.includes("\\")
        ? link
        : null;
    },
  },
};
</script>

<style scoped>
.notification-item {
  position: relative;
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) auto;
  align-items: start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--color-grey);
  border-radius: 12px;
  background: var(--color-light);
}

.notification-item--unread {
  background: color-mix(in srgb, var(--color-green) 3%, var(--color-light));
}

.notification-item__marker {
  width: 8px;
  height: 8px;
  margin-top: 5px;
  border-radius: 50%;
}

.notification-item--unread .notification-item__marker {
  background: var(--color-green);
}

.notification-item__content {
  min-width: 0;
  overflow-wrap: anywhere;
}

.notification-item__time {
  color: color-mix(in srgb, var(--color-dark) 65%, var(--color-light));
  font-size: 12px;
  white-space: nowrap;
}

.notification-item__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  line-height: 1.4;
}

.notification-item__groups {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.notification-item__read {
  position: relative;
  z-index: 1;
  align-self: center;
  color: color-mix(in srgb, var(--color-dark) 65%, var(--color-light));
}

.notification-item__group {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: color-mix(in srgb, var(--color-dark) 65%, var(--color-light));
  font-size: 12px;
  line-height: 1.4;
}

.notification-item__title {
  margin: 4px 0;
  font-size: 14px;
  line-height: 1.4;
}

.notification-item__link {
  position: absolute;
  inset: 0;
  border-radius: inherit;
}

.notification-item__link:hover {
  background: color-mix(in srgb, var(--color-dark) 3%, transparent);
}

.notification-item__link:focus-visible {
  outline: 2px solid var(--color-green);
  outline-offset: -2px;
}

.notification-item__body {
  margin: 0;
  color: color-mix(in srgb, var(--color-dark) 75%, var(--color-light));
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
}

@media (max-width: 640px) {
  .notification-item {
    padding: 10px 12px;
  }
}

/* Popup показывает краткое содержание, полная карточка остаётся на странице */
.notification-item--compact {
  padding: 10px 4px;
  gap: 8px;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.notification-item--compact + .notification-item--compact {
  border-top: 1px solid var(--color-grey);
}

.notification-item--compact .notification-item__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.notification-item--compact .notification-item__title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
}

.notification-item--compact .notification-item__title,
.notification-item--compact .notification-item__body {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.notification-item--compact .notification-item__body {
  font-size: 12px;
  white-space: normal;
  color: color-mix(in srgb, var(--color-dark) 65%, var(--color-light));
}

.notification-item--compact .notification-item__meta {
  order: 1;
  gap: 6px;
}

.notification-item--compact .notification-item__time {
  font-size: 11px;
}

</style>
